import * as fs from "node:fs/promises";
import { homedir } from "node:os";
import * as nodePath from "node:path";

import { Q5MError } from "./client.js";

export const DEFAULT_API_URL = "https://api.q5m.ai";

interface StoredConfig {
  apiUrl?: string;
  apiKey?: string;
  context?: string;
}

export interface ResolvedConfig {
  apiUrl: string;
  apiKey?: string;
  context: string;
  groupId?: string;
  credentialSource: "environment" | "config" | "none";
  path: string;
}

export interface ConfigOptions {
  env?: NodeJS.ProcessEnv;
  path?: string;
  home?: string;
}

function normalizeApiUrl(value: string): string {
  return value.trim().replace(/\/+$/, "");
}

export function resolveLoginApiUrl(env: NodeJS.ProcessEnv = process.env): string {
  const override = env.Q5M_API_URL?.trim();
  const developmentAllowed = env.Q5M_ALLOW_DEVELOPMENT_LOGIN === "1";
  if (!override) {
    if (developmentAllowed) {
      throw new Q5MError(
        "Q5M_ALLOW_DEVELOPMENT_LOGIN=1 requires Q5M_API_URL.",
        0,
        "usage_error",
      );
    }
    return DEFAULT_API_URL;
  }
  if (!developmentAllowed) {
    throw new Q5MError(
      "Unset Q5M_API_URL before q5m login, or set Q5M_ALLOW_DEVELOPMENT_LOGIN=1 for an explicit development login.",
      0,
      "usage_error",
    );
  }
  let parsed: URL;
  try {
    parsed = new URL(override);
  } catch {
    throw new Q5MError("Q5M_API_URL must be an absolute HTTP(S) URL.", 0, "usage_error");
  }
  if (
    (parsed.protocol !== "http:" && parsed.protocol !== "https:") ||
    parsed.username ||
    parsed.password ||
    parsed.hash
  ) {
    throw new Q5MError(
      "Q5M_API_URL must be an absolute HTTP(S) URL without credentials or a fragment.",
      0,
      "usage_error",
    );
  }
  return normalizeApiUrl(override);
}

export function resolveConfigPath(options: ConfigOptions = {}): string {
  if (options.path) return options.path;
  const env = options.env ?? process.env;
  const root = env.Q5M_CONFIG_HOME
    ? env.Q5M_CONFIG_HOME
    : env.XDG_CONFIG_HOME
      ? nodePath.join(env.XDG_CONFIG_HOME, "q5m")
      : nodePath.join(options.home ?? homedir(), ".config", "q5m");
  return nodePath.join(root, "config.json");
}

async function readStored(path: string): Promise<StoredConfig> {
  try {
    const parsed: unknown = JSON.parse(await fs.readFile(path, "utf8"));
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("config root must be an object");
    }
    for (const field of ["apiUrl", "apiKey", "context"]) {
      const value = (parsed as Record<string, unknown>)[field];
      if (value !== undefined && typeof value !== "string") {
        throw new Error(`${field} must be a string`);
      }
    }
    return parsed as StoredConfig;
  } catch (error) {
    const code = (error as NodeJS.ErrnoException).code;
    if (code === "ENOENT") return {};
    throw new Q5MError(
      `Cannot read q5m config at ${path}: ${(error as Error).message}`,
      0,
      "usage_error",
    );
  }
}

async function writeStored(path: string, config: StoredConfig): Promise<void> {
  const directory = nodePath.dirname(path);
  const createdDirectory = await fs.mkdir(directory, { recursive: true, mode: 0o700 });
  if (createdDirectory !== undefined) await fs.chmod(directory, 0o700);
  const temporary = `${path}.${process.pid}.${globalThis.crypto.randomUUID()}.tmp`;
  try {
    await fs.writeFile(temporary, `${JSON.stringify(config, null, 2)}\n`, {
      flag: "wx",
      mode: 0o600,
    });
    await fs.rename(temporary, path);
    await fs.chmod(path, 0o600);
  } catch (error) {
    await fs.rm(temporary, { force: true });
    throw error;
  }
}

export async function loadConfig(options: ConfigOptions = {}): Promise<ResolvedConfig> {
  const env = options.env ?? process.env;
  const path = resolveConfigPath(options);
  const stored = await readStored(path);
  const environmentKey = env.Q5M_API_KEY?.trim();
  const storedKey = stored.apiKey?.trim();
  const configuredUrl = env.Q5M_API_URL?.trim() || stored.apiUrl?.trim() || DEFAULT_API_URL;
  const context =
    env.Q5M_GROUP_ID?.trim() || env.Q5M_GROUP?.trim() || stored.context?.trim() || "personal";
  const normalizedContext = context === "personal" ? "personal" : context;
  return {
    apiUrl: normalizeApiUrl(configuredUrl),
    ...(environmentKey || storedKey ? { apiKey: environmentKey || storedKey } : {}),
    context: normalizedContext,
    ...(normalizedContext === "personal" ? {} : { groupId: normalizedContext }),
    credentialSource: environmentKey ? "environment" : storedKey ? "config" : "none",
    path,
  };
}

export async function saveAuthentication(
  apiUrl: string,
  apiKey: string,
  options: ConfigOptions = {},
): Promise<void> {
  const path = resolveConfigPath(options);
  const stored = await readStored(path);
  await writeStored(path, {
    ...stored,
    apiUrl: normalizeApiUrl(apiUrl),
    apiKey: apiKey.trim(),
  });
}

export async function clearAuthentication(options: ConfigOptions = {}): Promise<void> {
  const path = resolveConfigPath(options);
  const stored = await readStored(path);
  delete stored.apiKey;
  await writeStored(path, stored);
}

export async function saveContext(context: string, options: ConfigOptions = {}): Promise<void> {
  const path = resolveConfigPath(options);
  const stored = await readStored(path);
  await writeStored(path, {
    ...stored,
    context: context.trim() || "personal",
  });
}
